/**
 * Przelacznik motywu jasny / ciemny / automatyczny.
 *
 * Stan trzymamy w localStorage i odwzorowujemy na atrybut data-theme
 * dokumentu; sama paleta siedzi w style.css i korzysta z light-dark(),
 * wiec tryb "auto" to po prostu brak atrybutu.
 *
 * Wartosc startowa ustawia inline'owy skrypt w index.html - tutaj jest
 * juz tylko obsluga klikniec.
 */

export type ThemeMode = 'auto' | 'light' | 'dark'

const KEY = 'gmlweb.theme'
const MODES: ThemeMode[] = ['auto', 'light', 'dark']

const LABEL: Record<ThemeMode, string> = {
  auto: 'Motyw systemowy',
  light: 'Motyw jasny',
  dark: 'Motyw ciemny',
}

const ICON: Record<ThemeMode, string> = {
  auto: `<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.2"/>
         <path d="M8 2.8A5.2 5.2 0 0 1 8 13.2z" class="fill"/></svg>`,
  light: `<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="3.1"/>
          <path d="M8 1v1.9M8 13.1V15M1 8h1.9M13.1 8H15M3 3l1.35 1.35M11.65 11.65 13 13M13 3l-1.35 1.35M4.35 11.65 3 13"/></svg>`,
  dark: `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13.2 9.9A5.6 5.6 0 0 1 6.1 2.8 5.6 5.6 0 1 0 13.2 9.9z"/></svg>`,
}

export function currentMode(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY) as ThemeMode | null
    if (v && MODES.includes(v)) return v
  } catch { /* prywatny tryb przegladarki - zostajemy przy auto */ }
  return 'auto'
}

export function applyTheme(mode: ThemeMode) {
  const root = document.documentElement
  if (mode === 'auto') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', mode)
  try { localStorage.setItem(KEY, mode) } catch { /* j.w. */ }
  syncMetaColor()
}

/**
 * Pasek adresu na mobile bierze kolor stad - inaczej zostaje bialy.
 *
 * Czytamy tlo body, a nie zmienna --ground: getComputedStyle zwraca wartosc
 * wlasnej wlasciwosci CSS bez podstawienia, wiec dostalibysmy doslowny napis
 * "light-dark(#ecefec, #14171a)", ktorego przegladarka tu nie zrozumie.
 */
function syncMetaColor() {
  const meta = document.querySelector<HTMLMetaElement>('meta[name=theme-color]')
  if (!meta || !document.body) return
  const c = getComputedStyle(document.body).backgroundColor
  if (c) meta.content = c
}

/** Segmentowany przelacznik do wstawienia w naglowek widoku. */
export function themeToggleHTML(): string {
  const cur = currentMode()
  return `<div class="seg theme" role="group" aria-label="Motyw interfejsu">${
    MODES.map(m => `<button type="button" data-mode="${m}" title="${LABEL[m]}"
        aria-label="${LABEL[m]}" aria-pressed="${m === cur}">${ICON[m]}</button>`).join('')
  }</div>`
}

export function wireThemeToggle(root: ParentNode = document) {
  const box = root.querySelector<HTMLElement>('.theme')
  if (!box) return
  box.querySelectorAll<HTMLButtonElement>('button[data-mode]').forEach(b => {
    b.onclick = () => {
      const mode = b.dataset.mode as ThemeMode
      applyTheme(mode)
      box.querySelectorAll<HTMLButtonElement>('button[data-mode]').forEach(o =>
        o.setAttribute('aria-pressed', String(o === b)))
    }
  })
}

/** W trybie auto system moze zmienic motyw w locie - domykamy meta theme-color. */
export function watchSystemTheme() {
  window.matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => { if (currentMode() === 'auto') syncMetaColor() })
  syncMetaColor()
}
